import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import {
  LuAward,
  LuBookmark,
  LuBookmarkPlus,
  LuCheck,
  LuChevronRight,
  LuClock,
  LuExternalLink,
  LuFastForward,
  LuFileText,
  LuGraduationCap,
  LuMaximize,
  LuMinimize,
  LuPause,
  LuPlay,
  LuRewind,
  LuTriangleAlert,
  LuVolume2,
  LuVolumeX,
} from 'react-icons/lu';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import SegmentedControl from '../../components/ui/SegmentedControl';
import { db, auth } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useTranslation } from '../../i18n/LanguageContext';
import PoolQuiz from '../../components/PoolQuiz/PoolQuiz';
import { QUIZ_SOURCE } from '../../utils/questionPool';
import { TILE } from './components/ProfileUi';
import { LearningProgress, PlayOverlay, VideoButton, VolumeControl } from './education/VideoParts';

// Video bölümleri (zaman çizelgesi); başlıklar dil dosyasından
const SECTION_KEYS = ['introduction', 'coreConcepts', 'practice', 'summary'];

/**
 * SOP eğitimi (video): ilerleme, notlar ve yer imleri userProgress'te
 * saklanır; %80'den sonra departman soru havuzundan sınav açılır, geçilince
 * eğitim tamamlandı olarak işaretlenebilir. İşleyiş aynı, görünüm Tailwind.
 */
const EducationModule = ({
  show,
  onHide,
  sopData,
  onComplete,
}) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`profilePage.education.${key}`, vars);
  const { employeeData, employeeId } = useUserProfile();

  // Video player state
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const videoWrapperRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hoverTime, setHoverTime] = useState(null);
  const [videoError, setVideoError] = useState(false);
  const [isVideoFormat, setIsVideoFormat] = useState(true);

  // Learning state
  const [activeTab, setActiveTab] = useState('content');
  const [progress, setProgress] = useState(0);
  const [lastPosition, setLastPosition] = useState(0);
  const [notes, setNotes] = useState('');
  const [bookmarks, setBookmarks] = useState([]);
  const [activeSection, setActiveSection] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [certificateEarned, setCertificateEarned] = useState(false);

  // Video sections/chapters for the timeline
  const videoSections = [
    { title: 'Introduction', startTime: 0, endTime: 60 },
    { title: 'Core Concepts', startTime: 61, endTime: 180 },
    { title: 'Practical Application', startTime: 181, endTime: 300 },
    { title: 'Summary', startTime: 301, endTime: 360 },
  ];
  const sectionTitle = (index) => label(`sections.${SECTION_KEYS[index]}`);

  // Sınav soruları departman soru havuzundan gelir (bkz. components/PoolQuiz)

  // Load saved progress on component mount
  useEffect(() => {
    const loadProgress = async () => {
      if (!auth.currentUser || !sopData) return;

      try {
        const progressDocRef = doc(db, 'userProgress', `${auth.currentUser.uid}_${sopData.id}`);
        const progressDoc = await getDoc(progressDocRef);

        if (progressDoc.exists()) {
          const data = progressDoc.data();
          setLastPosition(data.lastPosition || 0);
          setProgress(data.progress || 0);
          setNotes(data.notes || '');
          setBookmarks(data.bookmarks || []);
          setCertificateEarned(data.certificateEarned || false);
        }
      } catch (error) {
        console.error('Error loading progress:', error);
      }
    };

    loadProgress();
  }, [sopData]);

  // Check if the file is a video when component mounts
  useEffect(() => {
    if (sopData?.fileUrl) {
      // Check if the file is a video based on fileType in sopData or file extension
      const isVideo =
        (sopData.fileType && sopData.fileType.includes('video')) ||
        sopData.fileUrl.toLowerCase().endsWith('.mp4');

      setIsVideoFormat(isVideo);
    }
  }, [sopData]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      setCurrentTime(video.currentTime);

      // Update progress percentage
      const progressPercent = Math.floor((video.currentTime / video.duration) * 100);
      setProgress(Math.max(progress, progressPercent));

      // Update active section
      const currentSection = videoSections.findIndex(
        (section) => video.currentTime >= section.startTime && video.currentTime <= section.endTime
      );

      if (currentSection !== -1) {
        setActiveSection(currentSection);
      }
    };

    const onLoadedMetadata = () => {
      setDuration(video.duration);

      // Set video to last watched position
      if (lastPosition > 0) {
        video.currentTime = lastPosition;
      }
    };

    const onEnded = () => {
      setIsPlaying(false);
      setProgress(100);
      saveProgress(100, video.currentTime);
    };

    const onError = () => {
      setVideoError(true);
      console.error('Error loading video');
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);

    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, lastPosition, progress]);

  // Handle progress bar hover
  useEffect(() => {
    const progressBar = progressRef.current;
    if (!progressBar) return;

    const handleMouseMove = (e) => {
      const rect = progressBar.getBoundingClientRect();
      const position = (e.clientX - rect.left) / rect.width;
      const time = position * duration;
      setHoverTime(time);
    };

    const handleMouseLeave = () => {
      setHoverTime(null);
    };

    progressBar.addEventListener('mousemove', handleMouseMove);
    progressBar.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      progressBar.removeEventListener('mousemove', handleMouseMove);
      progressBar.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [progressRef, duration]);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // Save progress to Firestore
  const saveProgress = async (progressValue, position) => {
    if (!auth.currentUser || !sopData) return;

    try {
      const progressDocRef = doc(db, 'userProgress', `${auth.currentUser.uid}_${sopData.id}`);
      await setDoc(progressDocRef, {
        userId: auth.currentUser.uid,
        sopId: sopData.id,
        sopName: sopData.name,
        progress: progressValue,
        lastPosition: position,
        notes,
        bookmarks,
        certificateEarned,
        lastUpdated: serverTimestamp(),
      }, { merge: true });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  // Format time in seconds to mm:ss
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return '0:00';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Video player controls
  const togglePlay = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      saveProgress(progress, videoRef.current.currentTime);
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;

    if (isMuted) {
      videoRef.current.volume = volume;
    } else {
      videoRef.current.volume = 0;
    }
    setIsMuted(!isMuted);
  };

  const seek = (time) => {
    if (!videoRef.current) return;

    videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleProgressClick = (e) => {
    if (!progressRef.current || !videoRef.current) return;

    const rect = progressRef.current.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    const time = position * duration;
    seek(time);
  };

  const skipForward = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime += 10;
  };

  const skipBackward = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime -= 10;
  };

  const toggleFullscreen = () => {
    const videoContainer = videoWrapperRef.current;
    if (!videoContainer) return;

    if (!document.fullscreenElement) {
      videoContainer.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('Failed to enter fullscreen mode:', err);
      });
    } else if (document.exitFullscreen) {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error('Failed to exit fullscreen mode:', err);
      });
    }
  };

  // Bookmark functions
  const addBookmark = () => {
    if (!videoRef.current) return;

    const newBookmark = {
      id: Date.now(),
      time: videoRef.current.currentTime,
      label: `${formatTime(videoRef.current.currentTime)} - Bookmark`,
    };

    const updatedBookmarks = [...bookmarks, newBookmark];
    setBookmarks(updatedBookmarks);
    saveProgress(progress, videoRef.current.currentTime);
    toast.success(label('toast.bookmarkAdded'));
  };

  const goToBookmark = (time) => {
    seek(time);
  };

  // Notes functions
  const handleNotesChange = (e) => {
    setNotes(e.target.value);
  };

  const saveNotes = () => {
    if (!videoRef.current) return;
    saveProgress(progress, videoRef.current.currentTime);
    toast.success(label('toast.notesSaved'));
  };

  // Quiz functions
  const startQuiz = () => {
    setQuizStarted(true);
  };

  // Havuz sınavı geçildi (veya havuz boş olduğu için atlandı)
  const handleQuizPassed = () => {
    setQuizStarted(false);
    setCertificateEarned(true);
    saveProgress(100, videoRef.current ? videoRef.current.currentTime : 0);
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete(sopData.id);
    }
    onHide();
  };

  const percentOf = (time) => (duration ? (time / duration) * 100 : 0);

  return (
    <Modal
      isOpen={Boolean(show)}
      onClose={onHide}
      size="xl"
      title={sopData?.name || label('title')}
      subtitle={[`SOP · v${sopData?.versionNumber ?? '-'}`, duration ? label('duration', { minutes: Math.floor(duration / 60), seconds: Math.floor(duration % 60) }) : null].filter(Boolean).join(' · ')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
          <LuGraduationCap aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onHide}>{t('common.close')}</Button>
          {progress >= 100 && !certificateEarned && (
            <Button variant="primary" onClick={startQuiz} className="max-sm:tw-flex-1">{label('takeQuizToComplete')}</Button>
          )}
          {certificateEarned && (
            <Button variant="success" onClick={handleComplete} className="max-sm:tw-flex-1">
              <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('markCompleted')}
            </Button>
          )}
        </>
      )}
    >
      <div className="tw-grid tw-gap-5 lg:tw-grid-cols-[minmax(0,1fr)_20rem]">
        <div className="tw-min-w-0 tw-space-y-4">
          <div ref={videoWrapperRef} className="tw-overflow-hidden tw-rounded-2xl tw-bg-black tw-shadow-lg">
            {isVideoFormat ? (
              <>
                <div className="tw-relative tw-aspect-video">
                  <video ref={videoRef} onClick={togglePlay} className="tw-h-full tw-w-full tw-bg-black">
                    <source src={sopData?.fileUrl} type="video/mp4" />
                  </video>
                  {videoError && (
                    <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-bg-black/80 tw-p-6 tw-text-center tw-text-white">
                      <LuTriangleAlert aria-hidden="true" className="tw-h-10 tw-w-10 tw-text-amber-400" />
                      <p className="tw-text-sm">{label('videoError')}</p>
                    </div>
                  )}
                  {!isPlaying && !videoError && <PlayOverlay label={label('play')} onClick={togglePlay} />}
                </div>

                {!videoError && (
                  <div className="tw-space-y-1 tw-bg-gradient-to-b tw-from-slate-900 tw-to-black tw-px-3 tw-pb-2 tw-pt-3">
                    {/* Zaman çizelgesi: bölümler, ilerleme ve imleç */}
                    <div ref={progressRef} onClick={handleProgressClick} className="tw-group tw-relative tw-h-4 tw-cursor-pointer">
                      <span className="tw-absolute tw-inset-x-0 tw-top-1/2 tw-h-1.5 -tw-translate-y-1/2 tw-rounded-full tw-bg-white/20" />
                      {duration > 0 && videoSections.map((section, index) => (
                        <span
                          key={section.title}
                          title={sectionTitle(index)}
                          style={{ left: `${percentOf(section.startTime)}%`, width: `${percentOf(section.endTime - section.startTime)}%` }}
                          className={`tw-absolute tw-top-1/2 tw-h-1.5 -tw-translate-y-1/2 tw-border-r-2 tw-border-black/60 ${activeSection === index ? 'tw-bg-white/15' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            seek(section.startTime);
                          }}
                          role="presentation"
                        />
                      ))}
                      <span className="tw-absolute tw-left-0 tw-top-1/2 tw-h-1.5 -tw-translate-y-1/2 tw-rounded-full tw-bg-teal-400" style={{ width: `${percentOf(currentTime)}%` }} />
                      <span className="tw-absolute tw-top-1/2 tw-h-3.5 tw-w-3.5 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-rounded-full tw-bg-white tw-shadow" style={{ left: `${percentOf(currentTime)}%` }} />
                      {hoverTime !== null && (
                        <span style={{ left: `${percentOf(hoverTime)}%` }} className="tw-pointer-events-none tw-absolute -tw-top-7 -tw-translate-x-1/2 tw-rounded-md tw-bg-white tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-tabular-nums tw-text-slate-900">
                          {formatTime(hoverTime)}
                        </span>
                      )}
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-0.5">
                      <VideoButton label={isPlaying ? label('pause') : label('play')} onClick={togglePlay}>
                        {isPlaying ? <LuPause aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuPlay aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
                      </VideoButton>
                      <VideoButton label={label('back10')} onClick={skipBackward}><LuRewind aria-hidden="true" className="tw-h-4 tw-w-4" /></VideoButton>
                      <VideoButton label={label('forward10')} onClick={skipForward}><LuFastForward aria-hidden="true" className="tw-h-4 tw-w-4" /></VideoButton>
                      <VolumeControl
                        muted={isMuted}
                        volume={volume}
                        onToggleMute={toggleMute}
                        onChange={handleVolumeChange}
                        muteLabel={label('mute')}
                        unmuteLabel={label('unmute')}
                        volumeLabel={label('volume')}
                        icon={LuVolume2}
                        mutedIcon={LuVolumeX}
                      />
                      <span className="tw-ml-2 tw-text-xs tw-font-medium tw-tabular-nums tw-text-white/80">
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </span>
                      <span className="tw-flex-1" />
                      <VideoButton label={label('addBookmark')} onClick={addBookmark}><LuBookmarkPlus aria-hidden="true" className="tw-h-4 tw-w-4" /></VideoButton>
                      <VideoButton label={isFullscreen ? label('exitFullscreen') : label('fullscreen')} onClick={toggleFullscreen}>
                        {isFullscreen ? <LuMinimize aria-hidden="true" className="tw-h-4 tw-w-4" /> : <LuMaximize aria-hidden="true" className="tw-h-4 tw-w-4" />}
                      </VideoButton>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-bg-slate-900 tw-px-6 tw-py-14 tw-text-center tw-text-white">
                <LuFileText aria-hidden="true" className="tw-h-12 tw-w-12 tw-text-white/70" />
                <p className="tw-max-w-md tw-text-sm tw-text-white/80">{label('notVideo')}</p>
                <a
                  href={sopData?.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-inline-flex tw-h-10 tw-items-center tw-gap-2 tw-rounded-full tw-bg-white tw-px-4 tw-text-sm tw-font-semibold tw-text-slate-900 tw-transition-transform hover:tw-scale-[1.03]"
                >
                  <LuExternalLink aria-hidden="true" className="tw-h-4 tw-w-4" />
                  {label('viewDocument')}
                </a>
              </div>
            )}
          </div>

          {isVideoFormat && !videoError && (
            <div className={`${TILE} tw-grid tw-gap-4 tw-p-4 sm:tw-grid-cols-2 sm:tw-items-center`}>
              <p className="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
                <span className="tw-text-slate-400 dark:tw-text-slate-500">{label('currentSection')}: </span>
                <strong className="tw-font-semibold tw-text-slate-900 dark:tw-text-white">{sectionTitle(activeSection) || sectionTitle(0)}</strong>
              </p>
              <LearningProgress label={label('learningProgress')} percent={progress} valueText={label('percent', { value: progress })} />
            </div>
          )}
        </div>

        <aside className="tw-min-w-0 tw-space-y-4">
          <SegmentedControl
            size="sm"
            layoutId="sop-training-tabs"
            label={label('tabsLabel')}
            value={activeTab}
            onChange={setActiveTab}
            options={[
              { value: 'content', label: label('tabs.content') },
              { value: 'notes', label: label('tabs.notes') },
              { value: 'bookmarks', label: label('tabs.bookmarks') },
            ]}
          />

          {activeTab === 'content' && (
            <div className="tw-space-y-4">
              <ul className="tw-space-y-1.5">
                {videoSections.map((section, index) => (
                  <li key={section.title}>
                    <button
                      type="button"
                      onClick={() => seek(section.startTime)}
                      className={`tw-w-full tw-rounded-2xl tw-border tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                        activeSection === index
                          ? 'tw-border-teal-500/40 tw-bg-teal-500/10'
                          : 'tw-border-slate-200 tw-bg-white/70 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.06]'
                      }`}
                    >
                      <span className="tw-flex tw-items-center tw-gap-3">
                        <span className={`tw-h-2.5 tw-w-2.5 tw-shrink-0 tw-rounded-full ${activeSection === index ? 'tw-bg-teal-500' : 'tw-bg-slate-300 dark:tw-bg-white/20'}`} />
                        <span className="tw-min-w-0 tw-flex-1">
                          <span className="tw-block tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{sectionTitle(index)}</span>
                          <span className="tw-block tw-text-xs tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">{formatTime(section.startTime)} – {formatTime(section.endTime)}</span>
                        </span>
                        <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
                      </span>
                      {activeSection === index && (
                        <span className="tw-mt-2 tw-block tw-h-1 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
                          <span
                            className="tw-block tw-h-full tw-rounded-full tw-bg-teal-500"
                            style={{ width: `${Math.min(100, Math.max(0, ((currentTime - section.startTime) / (section.endTime - section.startTime)) * 100))}%` }}
                          />
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>

              {progress >= 80 && !quizStarted && !certificateEarned && (
                <div className="tw-space-y-2 tw-rounded-2xl tw-border tw-border-violet-500/25 tw-bg-violet-500/[0.06] tw-p-4">
                  <p className="tw-text-sm tw-font-semibold tw-text-violet-900 dark:tw-text-violet-100">{label('quizPromptTitle')}</p>
                  <p className="tw-text-sm tw-text-violet-900/80 dark:tw-text-violet-100/80">{label('quizPromptText')}</p>
                  <Button variant="primary" size="sm" onClick={startQuiz}>{label('startQuiz')}</Button>
                </div>
              )}

              {certificateEarned && (
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-2xl tw-border tw-border-emerald-500/25 tw-bg-emerald-500/[0.07] tw-p-5 tw-text-center">
                  <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-amber-300 tw-to-amber-500 tw-text-white tw-shadow-lg">
                    <LuAward aria-hidden="true" className="tw-h-7 tw-w-7" />
                  </span>
                  <p className="tw-text-sm tw-font-semibold tw-text-emerald-900 dark:tw-text-emerald-100">{label('certificateTitle')}</p>
                  <p className="tw-text-xs tw-text-emerald-900/80 dark:tw-text-emerald-100/80">{label('certificateText')}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="tw-space-y-2">
              <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
                <p className="tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{label('notesTitle')}</p>
                <span className="tw-text-xs tw-text-slate-400">{label('notesHint')}</span>
              </div>
              <textarea
                value={notes}
                onChange={handleNotesChange}
                onBlur={saveNotes}
                placeholder={label('notesPlaceholder')}
                rows={10}
                className="tw-w-full tw-resize-y tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/80 tw-px-3.5 tw-py-3 tw-text-sm tw-text-slate-800 tw-shadow-none tw-outline-none focus:tw-ring-2 focus:tw-ring-teal-500/40 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-100"
              />
              <Button size="sm" onClick={saveNotes}>{label('saveNotes')}</Button>
            </div>
          )}

          {activeTab === 'bookmarks' && (
            <div className="tw-space-y-3">
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                <p className="tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{label('bookmarksTitle')}</p>
                <Button size="sm" onClick={addBookmark}>
                  <LuBookmarkPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
                  {label('addBookmarkShort')}
                </Button>
              </div>
              {bookmarks.length > 0 ? (
                <ul className="tw-space-y-1.5">
                  {bookmarks.map((bookmark) => (
                    <li key={bookmark.id}>
                      <button
                        type="button"
                        onClick={() => goToBookmark(bookmark.time)}
                        className="tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3 tw-py-2.5 tw-text-left tw-text-sm tw-transition-colors hover:tw-bg-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.06]"
                      >
                        <LuBookmark aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-amber-500" />
                        <span className="tw-font-semibold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{formatTime(bookmark.time)}</span>
                        <span className="tw-ml-auto tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-400"><LuClock aria-hidden="true" className="tw-h-3 tw-w-3" />{label('goTo')}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="tw-rounded-2xl tw-bg-slate-500/[0.06] tw-px-3.5 tw-py-3 tw-text-sm tw-text-slate-500 dark:tw-bg-white/[0.04] dark:tw-text-slate-400">{label('noBookmarks')}</p>
              )}
            </div>
          )}
        </aside>
      </div>

      {/* Okuma sonu sınavı: departman soru havuzundan rastgele sorular */}
      <PoolQuiz
        show={quizStarted && !certificateEarned}
        group={employeeData?.role}
        source={{ type: QUIZ_SOURCE.SOP, id: sopData?.id || '', name: sopData?.name || '' }}
        user={{
          uid: auth.currentUser?.uid,
          employeeId,
          name: `${employeeData?.eName || ''} ${employeeData?.eSurname || ''}`.trim(),
        }}
        onPassed={handleQuizPassed}
        onClose={() => setQuizStarted(false)}
      />
    </Modal>
  );
};

EducationModule.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  sopData: PropTypes.object,
  onComplete: PropTypes.func,
};

export default EducationModule;
